/**
 * useHotspots — W1.2
 *
 * Holt die Hotspots-Liste vom Backend (GET /api/v2/hotspots).
 * Query-Key enthält die Filter — Änderungen triggern automatisch Re-Fetch.
 *
 * S10-18: Typen importiert von @heliumtracker/api-types (v0.3.0+).
 * Lokale Typ-Duplikate wurden entfernt.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { components } from '@heliumtracker/api-types'
import type { SortKey, SortDir } from './hotspotSort'
import type { PlanLimit } from '../../components/PlanLimitBanner'

// --- Typen aus api-types ---
export type HotspotTokenRewards = components['schemas']['HotspotTokenRewards']

export type Hotspot = components['schemas']['Hotspot'] & {
  // S10-08: Guard-Status fields
  guard_status?: 'ok' | 'warning' | 'critical' | null
  failed_guards?: string[] | null
  // FIN-SWEEP.2 FE-4: beobachtet-Status (user_hotspots.watched) für Filter + Badge
  watched?: boolean
}
// Extended with 'warn' for UI use - derived from online_pct threshold in normalizeHotspot
export type HotspotStatus = 'online' | 'offline' | 'warn'
export type HotspotEra = 'post' | 'pre'
export type DeviceType = 'iot' | 'cbrs' | 'wifi' | 'wifiOutdoor'
// Stream H (R4): Sortier-Typen leben in hotspotSort.ts (siehe Kommentar dort).
export type { SortKey, SortDir }

export type PagyMeta = {
  page: number
  pages: number
  count: number
  items: number
}

export type HotspotsResponse = {
  collection: Hotspot[]
  data: Hotspot[]
  meta: {
    pagy: PagyMeta
    has_legacy_hotspots?: boolean
    /**
     * Issue #57: eigene Hotspots ueber dem Plan-Kontingent sind serverseitig
     * ausgeblendet. Nur gesetzt, wenn es solche Hotspots gibt.
     */
    plan_limit?: PlanLimit
  }
}

export type HotspotFilter = 'owned' | 'neighbour' | 'inactive' | 'affiliate'

export type HotspotsFilters = {
  q?: string
  status?: 'all' | HotspotStatus
  filter?: HotspotFilter
  device_type?: DeviceType | 'all'
  sort?: SortKey
  sort_dir?: SortDir
  page?: number
  per_page?: number
  /**
   * BUGFIX #59: Tag-Scope. Das Backend filtert seit jeher ueber ?tag_ids[]
   * (hotspots_controller.rb#index); der Hook hat den Parameter nur nie
   * durchgereicht, weshalb tag-gescopte Ansichten account-weite Listen
   * zeigten.
   */
  tag_ids?: number[]
  /**
   * Issue #39: zeigt ausschliesslich ausgeblendete eigene Hotspots
   * (manuell archiviert ODER durch den Downgrade-Softcap deaktiviert) fuer
   * die Archiv-Ansicht. Eigener Parameter, unabhaengig von `status`
   * (Online/Offline-Filter) und `filter` (affiliate/owned/...).
   */
  archived_only?: boolean
}

// --- Hook ---

type UseHotspotsResult = {
  data: Hotspot[] | undefined
  meta: HotspotsResponse['meta'] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useHotspots(filters: HotspotsFilters = {}, enabled = true): UseHotspotsResult {
  const { q, status, filter, page, per_page, sort, sort_dir, tag_ids, archived_only } = filters

  // Status 'all' und 'warn' werden nicht ans Backend gesendet (warn ist UI-only)
  const statusParam: 'online' | 'offline' | undefined =
    status === 'online' || status === 'offline' ? status : undefined

  const query = useApi<HotspotsResponse>(
    [
      'hotspots',
      { q, status: statusParam, filter, page, per_page, sort, sort_dir, tag_ids, archived_only },
    ],
    async () => {
      const result = await apiClient.GET('/api/v2/hotspots', {
        params: {
          query: {
            ...(q ? { q } : {}),
            ...(statusParam ? { status: statusParam } : {}),
            ...(archived_only ? { archived_only: true } : {}),
            ...(filter ? { filter } : {}),
            ...(page ? { page } : {}),
            ...(per_page ? { per_page } : {}),
            ...(tag_ids && tag_ids.length > 0 ? { tag_ids } : {}),
            // S10-09 / Stream H (R4): sort_by UND sort_dir gehen ans Backend.
            // 'name' wird mitgesendet, weil sort_dir sonst ohne Bezugsspalte
            // ankommt und die Serverreihenfolge ueber Seitengrenzen kippt.
            ...(sort ? { sort_by: sort } : {}),
            ...(sort_dir ? { sort_dir } : {}),
          },
        },
      })
      if (result.error) throw new Error('Hotspots fetch failed')
      if (!result.data) throw new Error('No data in response')
      // F051: Wrap in {data:} shape for useApi, cast to HotspotsResponse to handle {collection: [...]} runtime shape
      // F052: Normalize BE rewards_* → hnt_* to bridge BE/contract mismatch
      // F053: Derive status/online_pct from online boolean; 'warn' when online_pct < 50
      const normalizeHotspot = (h: Record<string, unknown>): Hotspot => {
        const online_pct = (h.online_pct as number) ?? ((h.online as boolean) ? 100 : 0)
        const status =
          (h.status as string) ??
          ((h.online as boolean) ? (online_pct < 50 ? 'warn' : 'online') : 'offline')
        return {
          ...(h as Hotspot),
          status: status as Hotspot['status'],
          online_pct,
          hnt_24h: (h.hnt_24h ?? h.rewards_24h ?? null) as number | null,
          hnt_7d: (h.hnt_7d ?? h.rewards_7d ?? null) as number | null,
          hnt_30d: (h.hnt_30d ?? h.rewards_30d ?? null) as number | null,
        }
      }
      const raw = result.data as unknown as HotspotsResponse
      const normalized: HotspotsResponse = {
        ...raw,
        collection: raw.collection?.map(normalizeHotspot),
        data: raw.data?.map(normalizeHotspot),
      }
      return { data: normalized }
    },
    // Hooks duerfen nicht bedingt aufgerufen werden (rules-of-hooks) — Aufrufer
    // schalten stattdessen per enabled-Flag, ohne Doppel-Fetch.
    { enabled },
  )

  return {
    data: query.data?.collection ?? query.data?.data ?? [],
    meta: query.data?.meta,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
